import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Plug, Unlink, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";

const BADGE_GREEN = "text-[#166534] bg-[#DCFCE7] border-[#BBF7D0]";
const BADGE_GRAY = "text-[#334155] bg-[#F1F5F9] border-[#E2E8F0]";

function TestResultLine({ testResult, settings }) {
  const { t } = useLang();
  const lastStatus = testResult?.status || settings.last_test_status;
  if (!lastStatus) return null;
  return (
    <div className="text-sm flex items-center gap-1.5" data-testid="jira-test-result">
      {lastStatus === "CONNECTED" ? (
        <>
          <CheckCircle2 className="h-4 w-4 text-[#166534]" />
          <span className="text-[#166534]">
            {t("jiraConnectedAs")} <b>{testResult?.account?.display_name || settings.last_test_account}</b>
            {settings.last_test_at && <span className="text-muted-foreground font-normal"> · {new Date(testResult ? Date.now() : settings.last_test_at).toLocaleString()}</span>}
          </span>
        </>
      ) : (
        <>
          <XCircle className="h-4 w-4 text-[#991B1B]" />
          <span className="text-[#991B1B]">{testResult?.error || settings.last_test_error || "Connection failed"}</span>
        </>
      )}
    </div>
  );
}

function ConnectionCard({ settings, form, setForm, busy, onSave, onTest, onDisconnect, testResult }) {
  const { t } = useLang();
  const configured = settings.configured;
  return (
    <div className="apha-card p-5 space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-semibold flex items-center gap-2">
          <Plug className="h-4 w-4" />{t("jiraConnection")}
        </h2>
        <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${configured ? BADGE_GREEN : BADGE_GRAY}`}
          data-testid="jira-status-badge">
          {configured ? t("jiraConfiguredBadge") : t("jiraMockBadge")}
        </span>
      </div>
      {!configured && (
        <p className="text-sm text-muted-foreground" data-testid="jira-not-configured-note">{t("jiraNotConfigured")}</p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>{t("jiraSiteUrl")}</Label>
          <Input data-testid="jira-site-url-input" placeholder="mysite.atlassian.net"
            value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label>{t("jiraAccountEmail")}</Label>
          <Input data-testid="jira-email-input" placeholder="you@company.com"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label>{t("jiraApiToken")}</Label>
          <Input type="password" data-testid="jira-token-input"
            placeholder={settings.api_token_set ? "••••••••  " + t("jiraTokenSaved") : "ATATT..."}
            value={form.api_token} onChange={(e) => setForm({ ...form, api_token: e.target.value })} />
          <p className="text-[11px] text-muted-foreground">{t("jiraTokenHint")}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="jira-save-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]"
          disabled={busy === "save" || !form.site_url || !form.email || (!form.api_token && !settings.api_token_set)}
          onClick={onSave}>{t("jiraSaveConnection")}</Button>
        <Button data-testid="jira-test-btn" variant="outline" disabled={!configured || busy === "test"} onClick={onTest}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy === "test" ? "animate-spin" : ""}`} />{t("testConnection")}
        </Button>
        {configured && (
          <Button data-testid="jira-disconnect-btn" variant="outline"
            className="text-[#991B1B] border-[#FECACA] hover:bg-[#FEF2F2] ml-auto"
            disabled={busy === "disconnect"} onClick={onDisconnect}>
            <Unlink className="h-3.5 w-3.5 mr-1.5" />{t("jiraDisconnect")}
          </Button>
        )}
      </div>
      <TestResultLine testResult={testResult} settings={settings} />
    </div>
  );
}

function MappingRow({ mapping, jiraProjects, configured, busy, onMap }) {
  const { t } = useLang();
  return (
    <TableRow data-testid={`jira-mapping-row-${mapping.project_key}`}>
      <TableCell>
        <div className="font-medium">{mapping.project_key}</div>
        <div className="text-xs text-muted-foreground">{mapping.project_name}</div>
      </TableCell>
      <TableCell>
        <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${mapping.mode === "REAL" ? BADGE_GREEN : BADGE_GRAY}`}
          data-testid={`jira-mode-badge-${mapping.project_key}`}>
          {mapping.mode === "REAL" ? "JIRA CLOUD" : t("jiraNotConnected")}
        </span>
      </TableCell>
      <TableCell>
        <Select value={mapping.jira_project_key || undefined}
          disabled={!configured || busy === `map-${mapping.project_id}`}
          onValueChange={(v) => onMap(mapping, v)}>
          <SelectTrigger className="w-56" data-testid={`jira-map-select-${mapping.project_key}`}>
            <SelectValue placeholder={t("jiraSelectProject")} />
          </SelectTrigger>
          <SelectContent>
            {mapping.mode === "REAL" && (
              <SelectItem value="__unmap__" data-testid={`jira-unmap-option-${mapping.project_key}`}>
                {t("jiraUnmap")}
              </SelectItem>
            )}
            {(jiraProjects || []).map((p) => (
              <SelectItem key={p.id} value={p.key}>{p.key} — {p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="text-sm">{mapping.mode === "REAL" ? (mapping.synced_issues ?? 0) : "—"}</TableCell>
      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
        {mapping.last_synced_at ? new Date(mapping.last_synced_at).toLocaleString() : "—"}
      </TableCell>
    </TableRow>
  );
}

function MappingTable({ mappings, jiraProjects, configured, busy, onMap, onSyncNow }) {
  const { t } = useLang();
  return (
    <div className="apha-card p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading font-semibold">{t("jiraProjectMapping")}</h2>
        <Button size="sm" data-testid="jira-sync-now-btn" variant="outline"
          disabled={!configured || busy === "sync"} onClick={onSyncNow}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy === "sync" ? "animate-spin" : ""}`} />{t("jiraSyncNow")}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">{t("jiraMappingHint")}</p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t("projects")}</TableHead>
            <TableHead>{t("jiraSource")}</TableHead>
            <TableHead>{t("jiraCloudProject")}</TableHead>
            <TableHead>{t("jiraSyncedIssues")}</TableHead>
            <TableHead>{t("jiraLastSync")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {mappings.map((m) => (
              <MappingRow key={m.project_id} mapping={m} jiraProjects={jiraProjects}
                configured={configured} busy={busy} onMap={onMap} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function AdminJira() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [jiraProjects, setJiraProjects] = useState(null);
  const [form, setForm] = useState({ site_url: "", email: "", api_token: "" });
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/jira/config");
      setData(r.data);
      if (r.data.settings.configured) {
        setForm((f) => ({ ...f, site_url: r.data.settings.site_url, email: r.data.settings.email }));
      }
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const configured = data?.settings?.configured;

  useEffect(() => {
    if (!configured) return;
    api.get("/admin/jira/projects")
      .then((r) => setJiraProjects(r.data))
      .catch((e) => { setJiraProjects([]); toast.error(apiError(e)); });
  }, [configured]);

  if (!data) return <Skeleton className="h-40 w-full" />;

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/admin/jira/config", form);
      toast.success("Jira connection saved");
      setForm((f) => ({ ...f, api_token: "" }));
      setTestResult(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const r = await api.post("/admin/jira/test");
      setTestResult(r.data);
      if (r.data.status === "CONNECTED") toast.success(`Connected as ${r.data.account?.display_name || ""}`);
      else toast.error(r.data.error || "Connection failed");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm(t("jiraDisconnectConfirm"))) return;
    setBusy("disconnect");
    try {
      await api.delete("/admin/jira/config");
      toast.success(t("jiraDisconnected"));
      setForm({ site_url: "", email: "", api_token: "" });
      setTestResult(null);
      setJiraProjects(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const setMapping = async (m, jiraKey) => {
    setBusy(`map-${m.project_id}`);
    try {
      if (jiraKey === "__unmap__") {
        await api.put(`/admin/jira/mapping/${m.project_id}`, { jira_project_key: null });
        toast.success(`${m.project_key} → ${t("jiraMockData")}`);
      } else {
        const jp = (jiraProjects || []).find((p) => p.key === jiraKey);
        const r = await api.put(`/admin/jira/mapping/${m.project_id}`, {
          jira_project_key: jiraKey, jira_project_name: jp?.name || jiraKey });
        if (r.data.sync_error) toast.warning(`Mapped, but sync failed: ${r.data.sync_error}`);
        else toast.success(`${m.project_key} → ${jiraKey} (${r.data.synced_issues} ${t("jiraSyncedIssues").toLowerCase()})`);
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const syncNow = async () => {
    setBusy("sync");
    try {
      const r = await api.post("/admin/jira/sync", {});
      const ok = r.data.results.filter((x) => x.status === "SYNCED");
      const failed = r.data.results.filter((x) => x.status === "FAILED");
      if (ok.length) toast.success(ok.map((x) => `${x.project_key}: ${x.issues}`).join(" · "));
      if (failed.length) toast.error(failed.map((x) => `${x.project_key}: ${x.error}`).join(" · "));
      if (!r.data.results.length) toast.info(t("jiraNoMapped"));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="admin-jira-tab">
      <ConnectionCard settings={data.settings} form={form} setForm={setForm} busy={busy}
        onSave={save} onTest={test} onDisconnect={disconnect} testResult={testResult} />
      <MappingTable mappings={data.mappings} jiraProjects={jiraProjects} configured={configured}
        busy={busy} onMap={setMapping} onSyncNow={syncNow} />
    </div>
  );
}
